import {
  buildProtocolServerPayload,
  joinListenAddress,
  mappingWidth,
  protocolServerFormFromConfig,
  validateProtocolServerForm,
} from "./plc-protocol-servers-model";

export const EDGE_MODBUS_SERVER_KIND = "modbus_tcp_server";
export const EDGE_MODBUS_SERVER_PROFILE = "modbus-tcp-server-v1";
export const EDGE_MODBUS_SERVER_V2_PROFILE = "modbus-tcp-server-v2";
export const EDGE_MODBUS_SERVER_V2_CAPABILITY = "modbus-tcp-server-v2";
const EDGE_MODBUS_SERVER_PROFILES = new Set([
  EDGE_MODBUS_SERVER_PROFILE,
  EDGE_MODBUS_SERVER_V2_PROFILE,
]);
const EDGE_MODBUS_SERVER_PROFILE_CAPABILITIES = Object.freeze([
	"connector-config-v1",
	"modbus-tcp-server",
]);
export const EDGE_MODBUS_SERVER_CAPABILITIES = Object.freeze([
	...EDGE_MODBUS_SERVER_PROFILE_CAPABILITIES,
	"edge-tag-catalog-v1",
	"edge-tag-identity-v1",
]);

const REGISTER_INTEGER_TYPES = new Set([
  "uint16", "int16", "bcd16", "uint32", "int32", "bcd32", "uint64", "int64",
]);
const REGISTER_FLOAT_TYPES = new Set(["float32", "float64"]);
const REGISTER_STRING_TYPES = new Set(["string"]);
const WRITABLE_TABLES = new Set(["coils", "holding_registers"]);
const BIT_TABLES = new Set(["coils", "discrete_inputs"]);

const asArray = (value) => (Array.isArray(value) ? value : []);
const cleanString = (value) => String(value || "").trim();

export function protocolServerTargetOptions(gateways) {
  return [
    { value: "local", label: "Local gateway" },
    ...asArray(gateways).map((gateway) => ({
      value: `edge:${gateway.id}`,
      label: `${cleanString(gateway.name) || "Unnamed runtime"} · ${gateway.status === "online" ? "Online" : "Offline"}`,
    })),
  ];
}

export function edgeGatewayIDFromTarget(value) {
  const target = cleanString(value);
  return target.startsWith("edge:") ? target.slice(5) : "";
}

export function normalizeEdgeModbusServerInventory(payload = {}) {
  const runtime = payload.runtime && typeof payload.runtime === "object" ? payload.runtime : {};
  const capabilities = asArray(payload.capabilities).map(cleanString).filter(Boolean);
  const servers = asArray(payload.connectors).filter(
    (connector) => connector?.kind === EDGE_MODBUS_SERVER_KIND
  );
  const server = servers.length === 1 ? servers[0] : null;
  const publicConfig = server?.public_configuration && typeof server.public_configuration === "object"
    ? server.public_configuration
    : null;
  return {
    connection: runtime.connection === "online" ? "online" : "offline",
    lastSeenAt: runtime.last_seen_at || "",
    snapshotStale: runtime.snapshot_stale !== false,
    snapshotError: cleanString(runtime.snapshot_error),
    capabilities,
    servers,
    server,
    publicConfig,
    ambiguous: servers.length > 1,
  };
}

export function edgeModbusServerReadiness(inventory, artifactCapabilities = []) {
  const normalized = inventory?.capabilities ? inventory : normalizeEdgeModbusServerInventory(inventory);
  const requiresV2 = asArray(artifactCapabilities).includes(EDGE_MODBUS_SERVER_V2_CAPABILITY) ||
    normalized.server?.applied?.profile === EDGE_MODBUS_SERVER_V2_PROFILE;
  const requiredCapabilities = [
    ...EDGE_MODBUS_SERVER_CAPABILITIES,
    ...(requiresV2 ? [EDGE_MODBUS_SERVER_V2_CAPABILITY] : []),
  ];
  const missingCapabilities = requiredCapabilities.filter(
    (capability) => !normalized.capabilities.includes(capability)
  );
  if (normalized.connection !== "online") {
	return {
	  ready: false,
	  code: "offline",
	  message: "The selected runtime is offline. Running reviews are blocked; an existing server can still be reviewed for safe Stop, and a desired Stop can be retried.",
	};
  }
  if (normalized.snapshotStale) {
	return {
	  ready: false,
	  code: "stale",
	  message: normalized.snapshotError || "The selected runtime capability snapshot is stale. Running reviews are blocked until a fresh heartbeat; safe Stop remains available.",
	};
  }
  if (missingCapabilities.length > 0) {
    return {
      ready: false,
      code: "unsupported",
      message: "This runtime has not advertised current signed Modbus TCP server support. Running reviews are blocked; safe Stop remains available for an existing server.",
      missingCapabilities,
    };
  }
  if (normalized.ambiguous) {
    return { ready: false, code: "ambiguous", message: "The runtime reported more than one Modbus TCP server. Resolve the conflicting server revisions before changing configuration." };
  }
  return { ready: true, code: "ready", message: "Fresh signed Modbus TCP server support confirmed." };
}

const isConcreteLiteralIP = (host) => {
  const value = cleanString(host);
  if (!value || value === "localhost" || value === "0.0.0.0" || value === "::") return false;
  if (value.includes(":")) return /^[0-9a-fA-F:]+$/.test(value);
  const octets = value.split(".");
  if (octets.length !== 4 || !octets.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255 && String(Number(part)) === part)) return false;
  const first = Number(octets[0]);
  return first < 224 || first > 239;
};

const hasCatchAllCIDR = (cidrs) => cidrs.some((cidr) => cidr === "0.0.0.0/0" || cidr === "::/0");

const canonicalIPv4Network = (cidr) => {
  const match = String(cidr).match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/);
  if (!match) return true;
  const octets = match.slice(1, 5);
  const prefix = Number(match[5]);
  if (prefix < 0 || prefix > 32 || octets.some((part) => Number(part) > 255 || String(Number(part)) !== part)) return false;
  const address = octets.reduce((value, part) => ((value << 8) | Number(part)) >>> 0, 0);
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  return ((address & mask) >>> 0) === address;
};

export function validateEdgeModbusServerForm(form) {
  const validation = validateProtocolServerForm(form);
  const payload = buildProtocolServerPayload(form);
  const formErrors = { ...validation.formErrors };
  if (!isConcreteLiteralIP(form?.listenHost)) {
    formErrors.listenHost = "Edge listeners require one concrete unicast IPv4 or IPv6 address present on the device.";
  }
  const idle = Number(String(form?.idleTimeoutMs ?? "").trim());
  if (!Number.isInteger(idle) || idle < 1000 || idle > 3600000) {
    formErrors.idleTimeoutMs = "Idle timeout must be a whole number from 1000 to 3600000.";
  }
  if (hasCatchAllCIDR(payload.source_cidrs)) {
    formErrors.sourceCidrs = "Edge listeners reject catch-all source networks. Enter only the specific client networks that need access.";
  }
  if (payload.source_cidrs.some((cidr) => !canonicalIPv4Network(cidr))) {
    formErrors.sourceCidrs = "IPv4 source networks must be canonical masked CIDRs (for example 10.0.0.0/24).";
  }
  if (form?.enabled && payload.source_cidrs.length === 0) {
    formErrors.sourceCidrs = "A running edge listener requires at least one specific source network.";
  }
  if (form?.enabled && payload.mappings.length === 0) {
    formErrors.mappings = "A running edge listener requires at least one tag mapping. Stop the desired listener if an empty map is intentional.";
  }
  const port = Number(String(form?.listenPort ?? "").trim());
  if (!Number.isInteger(port) || port < 1024 || port > 65535) {
    formErrors.listenPort = "Edge listener port must be a whole number from 1024 to 65535.";
  }
  const firstFormError = Object.values(formErrors).find(Boolean) || "";
  const firstMappingError = validation.mappingErrors.find(Boolean) || "";
  return {
    ...validation,
    valid: !firstFormError && !firstMappingError,
    message: firstFormError || firstMappingError,
    formErrors,
  };
}

const mappingDataType = (mapping) => {
  if (BIT_TABLES.has(mapping.table)) return "bool";
  if (REGISTER_INTEGER_TYPES.has(mapping.value_type)) return "int64";
  if (REGISTER_FLOAT_TYPES.has(mapping.value_type)) return "float64";
  if (REGISTER_STRING_TYPES.has(mapping.value_type)) return "string";
  return "";
};

const nativeAddress = (mapping) => `${mapping.table}:${mapping.address}:${mapping.value_type || "bool"}`;

export function buildEdgeModbusServerSpec(form, connectorID = "") {
  const payload = buildProtocolServerPayload(form);
  const config = {
    listen_address: payload.listen_address,
    unit_id: payload.unit_id,
    max_clients: payload.max_clients,
    request_timeout_ms: payload.request_timeout_ms,
    idle_timeout_ms: Number(form.idleTimeoutMs),
    source_cidrs: payload.source_cidrs,
  };
  const mappings = payload.mappings.map((mapping) => {
    const bit = BIT_TABLES.has(mapping.table);
    const nativeConfig = {
      table: mapping.table,
      address: mapping.address,
      value_type: bit ? "bool" : mapping.value_type,
      ...(!bit ? { byte_order: mapping.byte_order, word_order: mapping.word_order } : {}),
      ...(!bit && mapping.value_type === "string" ? {
        string_length: mapping.string_length,
        string_raw: mapping.string_raw === true,
        string_right_justify: mapping.string_right_justify === true,
      } : {}),
    };
    return {
      tag_path: mapping.tag_path,
      tag_role: "exposure",
      native_address: nativeAddress({ ...mapping, value_type: nativeConfig.value_type }),
      native_config: nativeConfig,
      flow: WRITABLE_TABLES.has(mapping.table) ? "bidirectional" : "observe",
      data_type: mappingDataType({ ...mapping, value_type: nativeConfig.value_type }),
      verify_readback: false,
    };
  }).sort((left, right) => {
    const paths = left.tag_path.toLowerCase().localeCompare(right.tag_path.toLowerCase());
    if (paths !== 0) return paths;
    if (left.flow !== right.flow) return left.flow.localeCompare(right.flow);
    return left.native_address.toLowerCase().localeCompare(right.native_address.toLowerCase());
  });
  const requiresV2 = mappings.some((mapping) =>
    ["bcd16", "bcd32", "string"].includes(mapping.native_config.value_type)
  );
  return {
    id: cleanString(connectorID),
    kind: EDGE_MODBUS_SERVER_KIND,
    name: "Modbus TCP server",
    desired_state: payload.enabled ? "running" : "stopped",
    config,
    mappings,
    resources: [{ key: `tcp-listen:${joinListenAddress(form.listenHost, form.listenPort)}`, mode: "exclusive" }],
    requirements: {
      profile: requiresV2 ? EDGE_MODBUS_SERVER_V2_PROFILE : EDGE_MODBUS_SERVER_PROFILE,
		capabilities: [
		  ...EDGE_MODBUS_SERVER_PROFILE_CAPABILITIES,
		  ...(requiresV2 ? [EDGE_MODBUS_SERVER_V2_CAPABILITY] : []),
		],
    },
  };
}

export function edgeModbusServerFormFromPublicConfig(publicConfig, desiredState = "stopped") {
  if (!publicConfig || typeof publicConfig !== "object") return null;
  return protocolServerFormFromConfig({
    ...publicConfig,
    enabled: desiredState === "running",
    mappings: asArray(publicConfig.mappings),
  });
}

export function edgeModbusServerTruth(inventory) {
  const readiness = edgeModbusServerReadiness(inventory || {});
  const server = inventory?.server;
  const applied = server?.applied || null;
  const desiredRunning = server?.desired_state === "running";
  const appliedRunning = applied?.actual_state === "running";
  const sameRevision = Number(server?.desired_revision || 0) > 0 &&
    Number(server.desired_revision) === Number(applied?.applied_revision || 0);
  const sameDigest = !!server?.desired_sha256 && server.desired_sha256 === applied?.applied_sha256;
  const profileMatches = EDGE_MODBUS_SERVER_PROFILES.has(applied?.profile);
  const reconciled = readiness.ready && server?.sync_state === "in_sync" && sameRevision && sameDigest && profileMatches;
  return {
    present: !!server,
    syncState: cleanString(server?.sync_state) || "unknown",
    desiredRevision: Number(server?.desired_revision || 0),
    appliedRevision: Number(applied?.applied_revision || 0),
    desiredRunning,
    actualState: cleanString(applied?.actual_state) || "unknown",
    listening: reconciled && desiredRunning && appliedRunning,
    reconciled,
    diagnostic: cleanString(server?.diagnostic || applied?.error),
    lastVerifiedExchange: applied?.last_verified_exchange || "",
  };
}

export function reviewMatchesEdgeModbusServerSpec(preview, spec) {
  if (!preview || !spec) return false;
  if (
    preview.kind !== EDGE_MODBUS_SERVER_KIND ||
    preview.profile !== spec.requirements.profile ||
    preview.desired_state !== spec.desired_state ||
    !Number.isInteger(preview.revision) || preview.revision <= 0 ||
    !/^[a-f0-9]{64}$/i.test(preview.sha256 || "") ||
    preview.confirmation_required !== true ||
    preview.no_application_authentication_acknowledgement_required !== (spec.desired_state === "running")
  ) return false;
  const expectedResources = JSON.stringify(spec.resources);
  if (JSON.stringify(preview.resources || []) !== expectedResources) return false;
  const expectedMappings = spec.mappings.map((mapping) => ({
    tag_path: mapping.tag_path,
    native_address: mapping.native_address,
    flow: mapping.flow,
    data_type: mapping.data_type,
    verify_readback: false,
    tag_role: mapping.tag_role,
  }));
  if (JSON.stringify(preview.mappings || []) !== JSON.stringify(expectedMappings)) return false;
  if (JSON.stringify(preview.public_configuration || null) !== JSON.stringify(spec.config)) return false;
  const expectedPublicMappings = spec.mappings.map((mapping) => ({
    tag_path: mapping.tag_path,
    table: mapping.native_config.table,
    address: mapping.native_config.address,
    value_type: mapping.native_config.value_type,
    ...(!BIT_TABLES.has(mapping.native_config.table) ? {
      byte_order: mapping.native_config.byte_order,
      word_order: mapping.native_config.word_order,
      ...(mapping.native_config.value_type === "string" ? {
        string_length: mapping.native_config.string_length,
        string_raw: mapping.native_config.string_raw === true,
        string_right_justify: mapping.native_config.string_right_justify === true,
      } : {}),
    } : {}),
    flow: mapping.flow,
    data_type: mapping.data_type,
    tag_role: mapping.tag_role,
  }));
  return JSON.stringify(preview.public_mappings || []) === JSON.stringify(expectedPublicMappings);
}

export function displayEdgeRevision(value) {
  const revision = Number(value || 0);
  return revision > 0 ? String(revision) : "—";
}

export function edgeModbusSecurityHistoryLabel(revision) {
  return revision?.no_application_authentication_acknowledged === true
    ? "Security acknowledgement enforced"
    : "";
}

export function edgeModbusReviewMode({ dirty, present, syncState }) {
  if (dirty) return "changed";
  if (present && syncState === "rejected") return "recovery";
  return "disabled";
}

export function edgeModbusReviewPresentation(mode) {
  if (mode === "recovery") {
    return {
      title: "Rejected revision can be recovered",
      button: "Review recovery revision…",
      message: "Recovery creates a higher immutable revision with the same content. It requires a fresh exact confirmation and, when Running, a fresh security acknowledgement. Retry remains available for the existing desired revision.",
    };
  }
  return {
    title: "Draft is valid",
    button: "Review edge revision…",
    message: "Review creates an immutable server-signed revision; a separate confirmation queues it.",
  };
}

export function mappingRegisterSpan(mapping) {
  return mappingWidth(mapping);
}
